import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { t, formatINR } from '../i18n';
import { toast } from 'sonner';
import { Search, Sparkles, ThumbsUp, ThumbsDown, Loader2, AlertTriangle, FileText, Languages, ExternalLink, RotateCcw, FileDown, Copy } from 'lucide-react';
import DocumentDetailModal from '../components/DocumentDetailModal';

const ROUTE_STYLE = {
  'FAQ': { class: 'free', label: 'FAQ' },
  'Semantic Cache': { class: 'free', label: 'CACHE' },
  'Metadata DB': { class: 'free', label: 'METADATA' },
  'Summary Lookup': { class: 'free', label: 'SUMMARY' },
  'Gemini Flash': { class: 'ai', label: 'GEMINI FLASH' },
  'GPT-4 Mini': { class: 'ai', label: 'GPT-4 MINI' },
  'Claude Haiku': { class: 'ai', label: 'CLAUDE HAIKU' },
  'Claude Sonnet': { class: 'legal', label: 'CLAUDE SONNET' },
};

export default function QueryPage() {
  const { lang } = useAuth();
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [fb, setFb] = useState(null);
  const [docsByName, setDocsByName] = useState({});
  const [selDoc, setSelDoc] = useState(null); // {id, page, snippet}
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [thread, setThread] = useState([]);

  useEffect(() => {
    axios.get(`${API}/suggested-questions`).then(r => setSuggested(r.data.suggestions || [])).catch(() => {});
    axios.get(`${API}/documents`).then(r => {
      const map = {};
      r.data.forEach(d => { map[d.name] = d.id; });
      setDocsByName(map);
    }).catch(() => {});
  }, []);

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text) return;
    setBusy(true); setFb(null);
    try {
      const { data } = await axios.post(`${API}/query`, { question: text, language: lang, session_id: sessionId });
      if (result) setThread(prev => [...prev, result]);
      setResult(data);
      setQ('');
    } catch (err) {
      toast.error(err?.response?.data?.detail?.toString() || 'Query failed');
    } finally {
      setBusy(false);
    }
  };

  const newConversation = () => {
    setThread([]); setResult(null); setFb(null); setQ('');
    setSessionId(crypto.randomUUID());
  };

  const exportAnswer = async (format) => {
    try {
      const resp = await axios.post(`${API}/export`, {
        title: result.question.slice(0, 60),
        question: result.question,
        answer: result.answer_en,
        citations: result.citations || [],
        format,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `answer.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch {
      toast.error('Export failed');
    }
  };

  const sendFeedback = async (rating) => {
    if (!result) return;
    setFb(rating);
    try {
      await axios.post(`${API}/feedback`, { query_id: result.query_id, rating });
      toast.success(t(lang, 'query.thanks'));
    } catch {
      setFb(null);
      toast.error('Could not submit feedback');
    }
  };

  return (
    <div className="fade-up" data-testid="query-page">
      <div className="data-label accent-line text-[#DDA079] mb-3">{t(lang, 'nav.ask')}</div>
      <h1 className="font-headings text-4xl text-white mb-2">{t(lang, 'query.title')}</h1>
      <p className="text-slate-400 max-w-2xl text-[15px] leading-relaxed mb-10">{t(lang, 'query.subtitle')}</p>

      {/* Search */}
      <form
        onSubmit={(e) => { e.preventDefault(); ask(); }}
        className="relative max-w-3xl"
      >
        <div className="absolute left-4 inset-y-0 flex items-center pointer-events-none z-10">
          <Search size={16} className="text-slate-500" />
        </div>
        <input
          data-testid="query-input"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder={t(lang, 'query.placeholder')}
          className="v-input pl-11 pr-32 py-3.5 text-[15px]"
          disabled={busy}
        />
        <button
          data-testid="query-submit"
          type="submit"
          disabled={busy || !q.trim()}
          className="btn-primary absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-4 py-2"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {busy ? t(lang, 'query.asking') : t(lang, 'query.ask')}
        </button>
      </form>

      {/* Suggested */}
      {!result && !busy && suggested.length > 0 && (
        <div className="mt-8 max-w-3xl">
          <div className="data-label mb-3">{t(lang, 'query.suggested')}</div>
          <div className="flex flex-wrap gap-2">
            {suggested.map((s, i) => (
              <button
                key={i}
                data-testid={`suggested-${i}`}
                onClick={() => ask(s)}
                className="text-[13px] text-slate-300 border border-white/10 hover:border-[#C17F59]/50 hover:text-white px-3 py-1.5 rounded-sm transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Conversation history (multi-turn thread) */}
      {thread.length > 0 && (
        <div className="mt-8 max-w-3xl space-y-2" data-testid="conversation-thread">
          <div className="flex items-center justify-between">
            <div className="data-label">Conversation · {thread.length + (result ? 1 : 0)} turns</div>
            <button data-testid="new-conversation" onClick={newConversation}
              className="text-[11px] font-mono uppercase tracking-wider text-slate-500 hover:text-[#DDA079] transition-colors flex items-center gap-1">
              <RotateCcw size={11} /> New conversation
            </button>
          </div>
          {thread.map((r, i) => (
            <div key={r.query_id || i} className="border border-white/5 rounded-sm px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[12px] text-white">{r.question}</span>
                <span className={`v-badge ${(ROUTE_STYLE[r.route] || { class: 'ai' }).class}`}>{(ROUTE_STYLE[r.route] || { label: r.route }).label || r.route}</span>
                <span className="v-badge cost">{r.cost_inr === 0 ? '₹0.00' : `₹${r.cost_inr.toFixed(2)}`}</span>
              </div>
              <p className="text-slate-400 text-[12px] line-clamp-2">{r.answer_en}</p>
            </div>
          ))}
        </div>
      )}

      {busy && (
        <div className="mt-10 max-w-3xl space-y-3">
          <div className="skeleton h-4 w-1/3 rounded-sm" />
          <div className="skeleton h-4 w-full rounded-sm" />
          <div className="skeleton h-4 w-5/6 rounded-sm" />
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="mt-10 max-w-3xl fade-up" data-testid="answer-card">
          {/* Meta bar */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {(() => {
              const rs = ROUTE_STYLE[result.route] || { class: 'ai', label: result.route?.toUpperCase() };
              return (
                <span data-testid="route-badge" className={`v-badge ${rs.class}`}>
                  ◆ {rs.label}
                </span>
              );
            })()}
            <span data-testid="cost-badge" className="v-badge cost">
              {result.cost_inr === 0 ? `${t(lang, 'query.free')} · ₹0.00` : `₹${result.cost_inr.toFixed(2)}`}
            </span>
            <span className="v-badge conf">
              {t(lang, 'query.confidence')} {Math.round(result.confidence * 100)}%
            </span>
            <span className="v-badge cost">
              {result.elapsed_ms}ms
            </span>
            <span className="v-badge cost">
              {result.model_used}
            </span>
            <span className="ml-auto flex items-center gap-1">
              <span className="data-label mr-1 flex items-center gap-1"><FileDown size={11} /> Export</span>
              {['docx', 'xlsx', 'pdf'].map(f => (
                <button key={f} data-testid={`export-${f}`} onClick={() => exportAnswer(f)}
                  className="text-[10px] font-mono uppercase tracking-wider border border-white/10 hover:border-[#C17F59]/60 hover:text-[#DDA079] text-slate-400 px-2 py-1 rounded-sm transition-colors">
                  {f === 'docx' ? 'Word' : f === 'xlsx' ? 'Excel' : 'PDF'}
                </button>
              ))}
            </span>
          </div>

          {result.routed_question && (
            <div className="text-[11px] font-mono text-slate-500 mb-3" data-testid="routed-question">
              interpreted as: “{result.routed_question}”
            </div>
          )}

          {/* Review warning */}
          {result.review_warning && (
            <div className="flex items-start gap-3 border border-[#EF4444]/30 bg-[#EF4444]/[0.04] px-4 py-3 mb-4 rounded-sm" data-testid="review-warning">
              <AlertTriangle size={15} className="text-red-400 mt-0.5 flex-shrink-0" />
              <div className="text-[13px] text-red-200">{t(lang, 'query.reviewWarn')}</div>
            </div>
          )}

          {/* Answer */}
          <div className="v-card p-6">
            <div className="data-label mb-2">Question</div>
            <div className="text-white text-[15px] mb-4" data-testid="asked-question">
              {result.question}
            </div>
            <div className="flex items-center justify-between mb-3">
              <div className="data-label">{t(lang, 'query.answer')}</div>
              <button
                data-testid="copy-answer-btn"
                onClick={() => { navigator.clipboard.writeText(result.answer_en); toast.success('Answer copied'); }}
                className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
                title="Copy answer only"
              >
                <Copy size={13} /> Copy
              </button>
            </div>
            <div className="text-slate-100 leading-relaxed whitespace-pre-wrap text-[15px]" data-testid="answer-text">
              {result.answer_en}
            </div>
          </div>

          {/* Translated */}
          {result.answer_translated && (
            <div className="v-card p-6 mt-3 border-[#C17F59]/30 bg-[#C17F59]/[0.03]">
              <div className="flex items-center gap-2 mb-3">
                <Languages size={13} className="text-[#DDA079]" />
                <span className="data-label text-[#DDA079]">{t(lang, 'query.translated')} · {lang === 'hi' ? 'हिन्दी' : 'తెలుగు'}</span>
              </div>
              <div className="text-slate-100 leading-relaxed whitespace-pre-wrap text-[15px]" data-testid="answer-translated">
                {result.answer_translated}
              </div>
            </div>
          )}

          {/* Citations */}
          {result.citations?.length > 0 ? (
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <FileText size={13} className="text-[#C17F59]" />
                <span className="data-label">{t(lang, 'query.sources')} ({result.citations.length})</span>
              </div>
              <div className="space-y-2">
                {result.citations.map((c, i) => {
                  const docId = docsByName[c.doc_name];
                  return (
                    <div
                      key={i}
                      role={docId ? 'button' : undefined}
                      tabIndex={docId ? 0 : undefined}
                      onClick={() => docId && setSelDoc({ id: docId, page: c.page, snippet: c.snippet })}
                      onKeyDown={(e) => { if (docId && (e.key === 'Enter' || e.key === ' ')) setSelDoc({ id: docId, page: c.page, snippet: c.snippet }); }}
                      className={`citation ${docId ? 'cursor-pointer hover:border-[#C17F59]/60 transition-colors group' : ''}`}
                      data-testid={`citation-${i}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-mono text-[11px] text-[#DDA079]">[Source {i+1}]</span>
                        <span className="text-[13px] text-white">{c.doc_name}</span>
                        <span className="data-label">{t(lang, 'common.page')} {c.page}</span>
                        <span className="data-label">§ {c.section}</span>
                        {docId && (
                          <span className="ml-auto flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500 group-hover:text-[#DDA079] transition-colors" data-testid={`citation-view-doc-${i}`}>
                            <ExternalLink size={10} /> View document
                          </span>
                        )}
                      </div>
                      {c.snippet && <div className="text-slate-400 text-[12px] mt-1 italic">"{c.snippet}…"</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="mt-6 text-slate-500 text-[13px]">{t(lang, 'query.noSources')}</div>
          )}

          {/* Feedback */}
          <div className="mt-6 flex items-center gap-3">
            <span className="data-label">{t(lang, 'query.feedback')}</span>
            <button
              data-testid="feedback-up"
              onClick={() => sendFeedback('up')}
              disabled={fb != null}
              className={`btn-ghost flex items-center gap-1 ${fb === 'up' ? 'text-emerald-400' : ''}`}
            >
              <ThumbsUp size={13} />
            </button>
            <button
              data-testid="feedback-down"
              onClick={() => sendFeedback('down')}
              disabled={fb != null}
              className={`btn-ghost flex items-center gap-1 ${fb === 'down' ? 'text-red-400' : ''}`}
            >
              <ThumbsDown size={13} />
            </button>
          </div>
        </div>
      )}

      {/* Full document viewer (portal) */}
      {selDoc && <DocumentDetailModal docId={selDoc.id} initialPage={selDoc.page} highlightText={selDoc.snippet} onClose={() => setSelDoc(null)} />}
    </div>
  );
}
